import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";

function IdleLogoutHandler({ timeoutMinutes = 15 }) {
  const [, navigate] = useLocation();
  const { logoutMutation, user } = useAuth();
  const timeoutRef = useRef<number | null>(null);
  const warningRef = useRef<number | null>(null);
  const countdownRef = useRef<number | null>(null);

  const [showPrompt, setShowPrompt] = useState(false);
  const [countdown, setCountdown] = useState(30); // seconds before logout
  const showPromptRef = useRef(showPrompt);

  // --- Main logout ---
  const logoutAndRedirect = () => {
    setShowPrompt(false);
    logoutMutation.mutate();
    localStorage.clear();
    sessionStorage.clear();
    navigate("/landing-page");
    setTimeout(() => window.location.reload(), 2000);
  };

  // --- Reset idle timer ---
  const resetTimer = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);

    localStorage.setItem("lastActivity", Date.now().toString());

    // Schedule warning 30s before logout
    const warnAfter = (timeoutMinutes * 60 - 30) * 1000;

    warningRef.current = window.setTimeout(() => {
      setShowPrompt(true);
      setCountdown(30);

      // Start countdown ticking every second
      countdownRef.current = window.setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownRef.current!);
            logoutAndRedirect();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, warnAfter);

    // Schedule actual logout
    timeoutRef.current = window.setTimeout(
      logoutAndRedirect,
      timeoutMinutes * 60 * 1000
    );
  };

  useEffect(() => {
    showPromptRef.current = showPrompt;
  }, [showPrompt]);

  useEffect(() => {
    if (!user) {
      clearTimeout(timeoutRef.current!);
      clearTimeout(warningRef.current!);
      clearInterval(countdownRef.current!);
      setShowPrompt(false);
      return;
    }
    // --- Sync across tabs ---
    const handleStorageChange = (e: StorageEvent) => {
      if (!user) return;

      if (e.key === "lastActivity") {
        resetTimer();
      }
      if (e.key === "staySignedIn") {
        setShowPrompt(false);
        clearInterval(countdownRef.current!);
        resetTimer();
      }
    };
    window.addEventListener("storage", handleStorageChange);

    // --- Handle tab visibility ---
    const handleVisibility = () => {
      if (!user) return;

      if (document.visibilityState === "visible") {
        const last = localStorage.getItem("lastActivity");
        if (last) {
          const diff = Date.now() - Number(last);
          const totalMs = timeoutMinutes * 60 * 1000;
          const warnMs = totalMs - 30 * 1000;
          if (diff >= totalMs) {
            logoutAndRedirect();
          } else if (diff >= warnMs) {
            setShowPrompt(true);
            setCountdown(Math.ceil((totalMs - diff) / 1000));
          } else {
            resetTimer();
          }
        }
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("focus", handleVisibility);

    const events = ["mousemove", "keydown", "touchstart"];
    const handleUserActivity = () => {
      // Only reset timer if prompt is NOT currently showing
      if (!showPromptRef.current) resetTimer();
    };

    events.forEach((event) =>
      window.addEventListener(event, handleUserActivity)
    );

    const lastActivity = localStorage.getItem("lastActivity");
    if (!lastActivity) {
      localStorage.setItem("lastActivity", Date.now().toString());
    }
    resetTimer();
    return () => {
      events.forEach((event) => window.removeEventListener(event, resetTimer));
      window.removeEventListener("storage", handleStorageChange);
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("focus", handleVisibility);

      clearTimeout(timeoutRef.current!);
      clearTimeout(warningRef.current!);
      clearInterval(countdownRef.current!);
    };
  }, [timeoutMinutes, user]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!("ReactNativeWebView" in window)) return;

    const handleRNMessage = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);

        // Expect messages like:
        // { type: "app-state", status: "background", timestamp: 1730904119000 }
        // { type: "app-state", status: "active", awayTimeSeconds: 42 }
        if (data.type === "app-state") {
          if (data.status === "background") {
            console.log("[RN Bridge] App went background at", data.timestamp);
          }

          if (data.status === "active") {
            console.log(
              "[RN Bridge] App resumed after",
              data.awayTimeSeconds,
              "s"
            );
            // --- If countdown dialog was open when backgrounded ---
            if (showPromptRef.current) {
              setCountdown((prev) => {
                const newValue = prev - data.awayTimeSeconds;
                if (newValue <= 0) {
                  logoutAndRedirect();
                  return 0;
                }
                return newValue;
              });
              return;
            }

            if (data.awayTimeSeconds >= timeoutMinutes * 60) {
              logoutAndRedirect();
            } else if (data.awayTimeSeconds >= 30) {
              resetTimer();
            } else {
              resetTimer();
            }
          }
        }

        if (data.type === "system" && data.event === "forced-logout") {
          console.warn("[RN Bridge] WebView was terminated — forcing logout");
          logoutAndRedirect();
        }
      } catch (err) {
        console.warn("[RN Bridge] Invalid message:", event.data);
      }
    };

    window.addEventListener("message", handleRNMessage);
    return () => window.removeEventListener("message", handleRNMessage);
  }, [timeoutMinutes, user]);

  const staySignedIn = () => {
    setShowPrompt(false);
    clearInterval(countdownRef.current!);
    resetTimer();
    localStorage.setItem("staySignedIn", Date.now().toString()); //
  };

  return (
    <>
      {showPrompt && (
        <Dialog open={showPrompt} onOpenChange={() => {}}>
          <DialogContent
            className=" space-y-2"
            onInteractOutside={(e) => e.preventDefault()}
            onEscapeKeyDown={(e) => e.preventDefault()}
            hideClose
          >
            <DialogTitle>Are you still there?</DialogTitle>
            <p>
              You’ll be logged out in{" "}
              <span className="font-semibold text-red-500">{countdown}</span>{" "}
              second{countdown !== 1 ? "s" : ""} unless you stay signed in.
            </p>
            <div className="flex justify-start gap-2">
              <Button
                variant="default"
                style={{}}
                onClick={staySignedIn}
                className="focus-visible:ring-0 focus-visible:outline-none"
              >
                Stay Signed In
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

export default IdleLogoutHandler;
